import { type InfiniteData, type QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { ConversationApi } from '@/Domain/api/Conversation/Conversation.api';
import { Event } from '@/Domain/api/Conversation/dto/Event.dto';
import { Paginated } from '@/Domain/api/Shared/dto/Paginated.dto';

const LIMIT = 100;
export const useConversationEvents = (conversationId: string) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery<
    Paginated<Event[]>,
    unknown,
    InfiniteData<Paginated<Event[]>>,
    QueryKey
  >({
    queryKey: ['conversations', conversationId, 'events'],
    queryFn: async ({ pageParam }) =>
      ConversationApi.getEvents(conversationId, pageParam).then(
        ({ data }) => data.events ?? { items: [], count: 0 },
      ),
    getNextPageParam: (params, pages) => {
      if (!params?.count) return undefined;
      const pagesCount = Math.ceil(params.count / LIMIT);
      return pagesCount > pages.length ? { limit: LIMIT, offset: pages.length * LIMIT } : undefined;
    },
    initialPageParam: { limit: LIMIT, offset: 0 },
    staleTime: 0,
  });

  const onLoadMore = useCallback(() => fetchNextPage(), []);

  return { events: data, hasNextPage, isFetchingNextPage, isLoading, onLoadMore };
};
